
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ticket, Plus, QrCode, TrendingUp, DollarSign, Users, Edit, Eye } from "lucide-react";

const Ticketing = () => {
  const events = [
    {
      id: 1,
      title: "Saturday Night Groove",
      date: "March 16, 2024",
      tickets: {
        general: { price: 150, sold: 280, total: 400 },
        vip: { price: 300, sold: 45, total: 100 },
        earlyBird: { price: 100, sold: 150, total: 150 }
      },
      revenue: 67500,
      status: "selling"
    },
    {
      id: 2,
      title: "Amapiano Fridays",
      date: "March 15, 2024", 
      tickets: {
        general: { price: 120, sold: 320, total: 450 },
        vip: { price: 250, sold: 25, total: 50 }
      },
      revenue: 44650,
      status: "sold-out"
    }
  ];

  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border-white/20 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Ticket className="w-5 h-5" />
              Ticketing & Sales
            </CardTitle>
            <Button className="bg-white text-black hover:bg-gray-200">
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick Stats */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">R112K</div>
                    <div className="text-gray-400 text-sm">Total Revenue</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Ticket className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">820</div>
                    <div className="text-gray-400 text-sm">Tickets Sold</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Users className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">75%</div>
                    <div className="text-gray-400 text-sm">Avg Capacity</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">+12%</div>
                    <div className="text-gray-400 text-sm">vs Last Month</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Event Tickets */}
          <div className="space-y-4">
            {events.map((event) => (
              <Card key={event.id} className="bg-white/5 border-white/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-medium">{event.title}</h3>
                      <p className="text-gray-400 text-sm">{event.date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={event.status === 'sold-out' ? 'destructive' : 'default'}
                        className={event.status === 'sold-out' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}
                      >
                        {event.status === 'sold-out' ? 'Sold Out' : 'On Sale'}
                      </Badge>
                      <Button size="sm" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button size="sm" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Ticket Tiers */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {Object.entries(event.tickets).map(([tier, data]) => (
                      <div key={tier} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-medium capitalize">{tier.replace(/([A-Z])/g, ' $1')}</h4>
                          <span className="text-white font-bold">R{data.price}</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Sold</span>
                            <span className="text-white">{data.sold}/{data.total}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2">
                            <div
                              className="bg-white h-2 rounded-full text-black"
                              style={{ width: `${(data.sold / data.total) * 100}%` }}
                            ></div>
                          </div>
                          <div className="text-right">
                            <span className="text-gray-400 text-sm">
                              {Math.round((data.sold / data.total) * 100)}% sold
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Revenue & QR Code */}
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <div className="text-white font-medium">Total Revenue</div>
                      <div className="text-2xl font-bold text-green-400">R{event.revenue.toLocaleString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                        <QrCode className="w-4 h-4 mr-1" />
                        QR Codes
                      </Button>
                      <Button size="sm" className="bg-white text-black hover:bg-gray-200">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Analytics
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Create New Event Form */}
          <Card className="bg-white/5 border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-lg">Create New Event</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Event Title</label>
                  <input
                    type="text"
                    placeholder="Enter event name"
                    className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Event Date</label>
                  <input
                    type="datetime-local"
                    className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-white text-sm font-medium mb-2 block">Ticket Tiers</label>
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-3">
                    <input
                      type="text"
                      placeholder="Tier name"
                      className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white placeholder-gray-400"
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white placeholder-gray-400"
                    />
                    <input
                      type="number"
                      placeholder="Quantity"
                      className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white placeholder-gray-400"
                    />
                    <Button variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button className="bg-white text-black hover:bg-gray-200">
                  Create Event
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                  Save Draft
                </Button>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

export default Ticketing;
